// SETTINGS V2 — Doprava (Shipping) page — SETTINGS_V2_SP9. Replaces the SP0
// ComingSoonGhostV2 for /settings-v2/shipping. Read-only summary of the 3 shipping
// methods (designer PageDoprava, settings-pages-c.jsx 128-144); ALL edits go through
// the 880px ShippingDialogV2. Binds the reused customerShippingOptions slice 100%
// UNCHANGED. FIX #1 lives in the dialog (setSuccess on the fulfilled PUT); this page
// renders that success via the existing `message={success}` toast binding that was
// previously never fed. Inline styles + scoped CSS-var tokens + v2 atoms + shared
// ActionToast/Loader (exempt). No MUI/sx/dt/Tailwind/moment. Page ROOT carries NO
// animation (SP2 toast-stacking rule).
import React, { useEffect } from "react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import ActionToast from "../../../../shared/components/ActionToast";
import Loader from "../../../../shared/components/Loader";
import { NotificationMessage, ShippingOptionCode } from "../../../../shared/models/models";
import {
  getShippingOptions,
  setError,
  setSuccess,
} from "../../customerShippingOptionsSlice";
import { openDialog } from "../settingsV2UiSlice";
import { SCard } from "../atoms";
import ShippingMethodSectionV2 from "./ShippingMethodSectionV2";
import ShippingDialogV2 from "../dialogs/ShippingDialogV2";

const METHOD_ORDER: ShippingOptionCode[] = [
  ShippingOptionCode.standart,
  ShippingOptionCode.express,
  ShippingOptionCode.selfPickUp,
];

const ShippingPageV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate: t } = useTranslation("settings");
  const { isPhone } = useBreakpoint();

  const { shippingOptions, loading, success, error } = useAppSelector(
    (s) => s.customerShippingOptions
  );

  // Mount fetch (parity — SEC-REQ-2). `dispatch` is stable ⇒ runs once.
  useEffect(() => {
    dispatch(getShippingOptions());
  }, [dispatch]);

  const editButton = (
    <button
      type="button"
      className="btn btn-primary"
      onClick={() => dispatch(openDialog("shipping"))}
      style={{
        background: "var(--mod-accent)",
        borderColor: "var(--mod-accent)",
        whiteSpace: "nowrap",
      }}
    >
      {t("settings:editShippingOptions")}
    </button>
  );

  return (
    // Page root: NO animation (SP2 §2 toast-stacking rule).
    <div style={{ display: "flex", flexDirection: "column", gap: 11, minWidth: 0, flex: 1 }}>
      <ActionToast
        open={!!success}
        variant="success"
        message={success || ""}
        onClose={() => dispatch(setSuccess(undefined))}
        duration={6000}
      />
      <ActionToast
        open={!!error}
        variant="error"
        message={NotificationMessage.error(error).message || ""}
        onClose={() => dispatch(setError(undefined))}
        duration={6000}
      />

      <SCard
        title={t("settings:shipping.title")}
        hint={t("settings:shipping.description")}
        action={editButton}
        bleed
        style={{ animation: "sv2-card-reveal 0.4s ease-out both" }}
      >
        {loading ? (
          <div style={{ display: "grid", placeItems: "center", padding: "40px 0" }}>
            <Loader />
          </div>
        ) : (
          <>
            <div style={{ padding: "0 20px 4px" }} />
            {METHOD_ORDER.map((code) => (
              <ShippingMethodSectionV2
                key={code}
                code={code}
                options={shippingOptions?.options ?? []}
                countryId={shippingOptions?.countryId ?? 0}
                warehouse={shippingOptions?.warehouse}
                isPhone={isPhone}
              />
            ))}
            {/* Mono footer strip (designer PageDoprava 138-140). */}
            <div
              style={{
                padding: "9px 16px",
                borderTop: "1px solid var(--border-soft)",
                background: "var(--bg-soft)",
                fontFamily: "var(--font-mono)",
                fontSize: 9.5,
                color: "var(--faint)",
                letterSpacing: "0.04em",
                borderRadius: "0 0 12px 12px",
              }}
            >
              {t("settings:shipping.footnote")}
            </div>
          </>
        )}
      </SCard>

      {/* Edit dialog reads its own open-state (openDialog === "shipping") from Redux. */}
      <ShippingDialogV2 />
    </div>
  );
};

export default ShippingPageV2;
